import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification, Tree, Modal } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AppSelect } from '../../constants/appSelect';
import { newObjectKeys, formatDateVN } from '../../utils/utility';
import { deleteMenu, getMenus, getAllMenus, updateMenu } from '../../services/menu';
import isEqual from 'lodash/isEqual';

const itemName = 'Menu';
const createItemLink = '/cms/quan-ly-menu/them-menu';
const editItemLink = '/cms/quan-ly-menu/sua-menu';
const { Text } = Typography;

const MenuList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({ page: 1, limit: 20 });
  const [openTree, setOpenTree] = useState(false);

  // Initialize params from URL on first load
  useEffect(() => {
    const page = Number(searchParams.get('page')) || 1;
    const limit = Number(searchParams.get('limit')) || 20;
    const keyword = searchParams.get('keyword') || undefined;
    const statusStr = searchParams.get('status');
    const statusVal = statusStr !== null ? Number(statusStr) : -1;
    const parentStr = searchParams.get('parent_id');
    const parentVal = parentStr !== null ? Number(parentStr) : -1;

    const next = { page, limit };
    if (keyword) Object.assign(next, { keyword });
    if (statusVal >= 0) Object.assign(next, { status: [statusVal] });
    if (parentVal > 0) Object.assign(next, { parent_id: parentVal });

    setParams(next);
    formSearch.setFieldsValue({
      keyword: keyword,
      status: statusVal >= 0 ? statusVal : -1,
      parent_id: parentVal > 0 ? parentVal : -1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push params to URL when they change
  useEffect(() => {
    if (!params) return;
    const sp = {
      page: String(params.page || 1),
      limit: String(params.limit || 20),
    };
    if (params.keyword) sp.keyword = params.keyword;
    if (Array.isArray(params.status) && params.status.length > 0 && params.status[0] >= 0) {
      sp.status = String(params.status[0]);
    }
    if (typeof params.parent_id === 'number' && params.parent_id > 0) {
      sp.parent_id = String(params.parent_id);
    }
    setSearchParams(sp, { replace: true });
  }, [params, setSearchParams]);

  const { data: dataMenus, isPending, refetch } = useQuery({ queryKey: ['dataMenus', params], queryFn: () => getMenus(params) });

  // Supporting data: all menus map for parent name
  const { data: allMenusRes, refetch: refetchAllMenus } = useQuery({ queryKey: ['allMenus'], queryFn: getAllMenus, staleTime: 1000 * 60 * 10 });

  // Ensure tree data is refreshed when popup opens
  useEffect(() => {
    if (openTree) {
      refetchAllMenus();
    }
  }, [openTree, refetchAllMenus]);

  // Also refresh tree data whenever the list data set changes (create/update/delete elsewhere)
  useEffect(() => {
    refetchAllMenus();
  }, [dataMenus, refetchAllMenus]);
  const menuMap = useMemo(() => {
    return (allMenusRes?.data || []).reduce((acc, cur) => {
      acc[cur?.id] = cur;
      return acc;
    }, {});
  }, [allMenusRes]);

  // Build tree data for Tree view
  const treeData = useMemo(() => {
    const items = (allMenusRes?.data || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const byParent = items.reduce((acc, cur) => {
      const p = Number(cur.parent_id || 0);
      acc[p] = acc[p] || [];
      acc[p].push(cur);
      return acc;
    }, {});
    const build = (parentId = 0) => {
      return (byParent[parentId] || []).map((item) => {
        const isActive = Number(item.status) === 1;
        return {
          key: String(item.id),
          title: <span style={{ color: isActive ? undefined : '#dc2626' }}>{item.title}</span>,
          data: item,
          children: build(Number(item.id)),
        };
      });
    };
    return build(0);
  }, [allMenusRes]);

  const { mutateAsync: mutateUpdateOrder } = useMutation({
    mutationFn: ({ id, payload }) => updateMenu(id, payload),
  });

  const handleDrop = useCallback(
    async (info) => {
      try {
        const dragKey = Number(info.dragNode.key);
        const dropKey = Number(info.node.key);
        const dropToGap = info.dropToGap;

        const all = (allMenusRes?.data || []).slice();
        const byId = all.reduce((acc, cur) => {
      acc[cur.id] = cur;
      return acc;
    }, {});

        if (!dropToGap) {
          // Drop ON node: make child of the target, place at the end
          const newParentId = dropKey;
          const siblings = all.filter((x) => Number(x.parent_id || 0) === newParentId).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          const newOrder = (siblings[siblings.length - 1]?.order ?? 0) + 1;
          await mutateUpdateOrder({ id: dragKey, payload: { parent_id: newParentId, order: newOrder } });
        } else {
          // Drop BETWEEN nodes: same parent as target, insert before/after target
          const target = byId[dropKey];
          if (!target) return;
          const parentId = Number(target.parent_id || 0);
          const siblings = all
            .filter((x) => Number(x.parent_id || 0) === parentId && x.id !== dragKey)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

          // Determine index of target within siblings
          const targetIndex = siblings.findIndex((x) => x.id === dropKey);
          const dropPosArr = String(info.node.pos).split('-');
          const dropPosIndex = Number(dropPosArr[dropPosArr.length - 1]);
          const relative = Number(info.dropPosition) - dropPosIndex; // -1: before, 1: after

          let insertIndex = targetIndex;
          if (relative >= 1) insertIndex = targetIndex + 1;

          // Insert dragged node
          const dragged = byId[dragKey];
          if (!dragged) return;
          const newSiblings = [...siblings];
          newSiblings.splice(insertIndex, 0, { ...dragged, parent_id: parentId });

          // Re-assign orders sequentially and push updates for affected items (including dragged)
          const updates = [];
          newSiblings.forEach((item, idx) => {
            const desiredOrder = idx + 1;
            if (item.id === dragKey) {
              updates.push(mutateUpdateOrder({ id: item.id, payload: { parent_id: parentId, order: desiredOrder } }));
            } else if ((item.order ?? 0) !== desiredOrder) {
              updates.push(mutateUpdateOrder({ id: item.id, payload: { order: desiredOrder } }));
            }
          });
          await Promise.all(updates);
        }

        notification.success({ message: 'Cập nhật thứ tự menu thành công!' });
        refetch();
        refetchAllMenus();
      } catch (e) {
        notification.error({ message: 'Không thể cập nhật thứ tự, vui lòng thử lại!' });
      }
    },
    [allMenusRes, mutateUpdateOrder, refetch, refetchAllMenus]
  );

  const { mutate: mutateDelete } = useMutation({
    mutationFn: deleteMenu,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Xóa ${itemName} thành công!` });
        refetch();
        refetchAllMenus();
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.keyword) Object.assign(temp, { keyword: values?.keyword });
    if (values?.status >= 0) Object.assign(temp, { status: [values?.status] });
    if (values?.parent_id > 0) Object.assign(temp, { parent_id: values?.parent_id });

    if (!isEqual(temp, params)) {
      setParams(temp);
      return;
    }
    refetch();
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'id',
      key: 'stt',
      width: 70,
      fixed: 'left',
      align: 'center',
      ellipsis: true,
      onHeaderCell: () => ({ style: { maxWidth: 70 } }),
      onCell: () => ({ style: { maxWidth: 70, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }),
      render: (_, __, index) => (params?.page - 1) * params?.limit + index + 1,
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      render: (_, { title, id }) => (
        <Link to={`${editItemLink}/${id}`} className="text-[#1677FF]">
          {title}
        </Link>
      ),
      minWidth: 150,
      width: 250,
      fixed: 'left',
    },
    {
      title: 'Menu cha',
      dataIndex: 'parent_id',
      key: 'parent',
      render: (_, { parent_id }) => {
        const parent = parent_id ? menuMap[parent_id] : null;
        return <span>{parent?.title || '—'}</span>;
      },
      minWidth: 180,
      width: 220,
    },
    {
      title: 'Link',
      dataIndex: 'link',
      key: 'link',
      render: (v) => {
        const s = typeof v === 'string' ? v : '';
        const truncated = s.length > 60 ? s.slice(0, 60) + '...' : s;
        return <Text title={s}>{truncated}</Text>;
      },
      minWidth: 150,
    },
    {
      title: 'Thứ tự',
      dataIndex: 'order',
      key: 'order',
      width: 90,
      align: 'center',
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (_, { updated_at }) => <Space direction="vertical">{updated_at && <Text className="text-[#667085]">{formatDateVN(updated_at)}</Text>}</Space>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag className="px-2 py-1" color={(AppSelect.STATUS || []).find((item) => item?.value === status)?.color}>
          {(AppSelect.STATUS || []).find((item) => item?.value === status)?.label}
        </Tag>
      ),
      width: 90,
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`${editItemLink}/${record?.id}`)}>
            <EditOutlined />
          </Button>
          {record?.status !== 2 && (
            <Button type="text" onClick={() => mutateDelete(record?.id)}>
              <DeleteOutlined />
            </Button>
          )}
        </Space>
      ),
      width: 90,
      fixed: 'right',
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách {itemName}</h1>
        <Space>
          <Button onClick={() => setOpenTree(true)}>
            Cây Menu
          </Button>
          <Link to={createItemLink}>
            <Button type="primary">
              <PlusOutlined />
              Thêm {itemName}
            </Button>
          </Link>
        </Space>
      </div>

      <Form name="search_menus" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="keyword" label={<Text>Từ khóa</Text>}>
              <Input placeholder="Nhập từ khóa tìm kiếm..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="parent_id" label={<Text>Menu cha</Text>} initialValue={-1}>
              <Select
                showSearch
                allowClear
                placeholder="Chọn menu cha"
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[
                  { value: -1, label: 'Tất cả' },
                  ...((allMenusRes?.data || []).map((m) => ({ value: m.id, label: m.title })) || []),
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: -1, label: 'Tất cả' }, ...(AppSelect.STATUS || [])]}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params && Object.values(newObjectKeys(params, ['keyword', 'status', 'parent_id'])).some((value) => value) && (
              <Button
                htmlType="button"
                onClick={() => {
                  formSearch.resetFields();
                  setParams({ page: 1, limit: params.limit });
                }}
              >
                <DeleteOutlined />
                Xóa tìm kiếm
              </Button>
            )}
          </Col>
        </Row>
      </Form>

      <Modal
        title="Cây Menu"
        open={openTree}
        onCancel={() => setOpenTree(false)}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Tree
          draggable
          blockNode
          treeData={treeData}
          onDrop={handleDrop}
          defaultExpandAll
        />
        <div className="mt-3 text-sm text-[#667085]">Lưu ý: Menu không active hoặc đã xóa sẽ hiển thị màu đỏ.</div>
      </Modal>

      <TableCustom columns={columns} dataSource={dataMenus?.data ?? []} totalItem={dataMenus?.total ?? 0} loading={isPending} params={params} setParams={setParams} />
    </div>
  );
};

export default MenuList;
